import React from 'react';
import type { BiomarkerMeta } from '../../data/biomarkerCatalogue';
import type { BiomarkerHistoryPoint } from '../../utils/biomarkerHistory';

/**
 * Small trend sparkline for a single biomarker over time. The healthy reference
 * band is shaded green behind the line; each reading is a dot coloured by its
 * status. Line is drawn in an SVG (stretched, non-scaling stroke); dots are
 * positioned divs so they stay circular regardless of width — same technique as
 * BiomarkerRangeBar.
 *
 * Render only when there are >= 2 points (a single reading has no trend).
 */

interface Props {
  meta: BiomarkerMeta;
  points: BiomarkerHistoryPoint[];
}

const PLOT_HEIGHT = 96;
const HEALTHY_FILL = 'rgba(79, 93, 24, 0.16)';
const LINE_COLOR = 'rgba(26, 26, 24, 0.55)';

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function formatShortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}

export default function BiomarkerTrend({ meta, points }: Props) {
  if (points.length < 2) return null;

  const { axisMin, axisMax, unit } = meta;
  const span = axisMax - axisMin || 1;
  const ref = points[points.length - 1].status.ref;

  const valueToYPercent = (v: number) => (1 - clamp01((v - axisMin) / span)) * 100;
  const xPercent = (i: number) => (points.length === 1 ? 50 : (i / (points.length - 1)) * 100);

  const lowY = ref.low != null ? valueToYPercent(ref.low) : 100;
  const highY = ref.high != null ? valueToYPercent(ref.high) : 0;
  const bandTop = Math.min(lowY, highY);
  const bandHeight = Math.abs(lowY - highY);

  const linePoints = points.map((p, i) => `${xPercent(i)},${valueToYPercent(p.value)}`).join(' ');

  return (
    <div>
      <div style={{ position: 'relative', height: PLOT_HEIGHT }}>
        {/* Shaded healthy band */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${bandTop}%`,
            height: `${bandHeight}%`,
            background: HEALTHY_FILL,
            borderTop: '1px dashed rgba(79, 93, 24, 0.5)',
            borderBottom: '1px dashed rgba(79, 93, 24, 0.5)',
          }}
        />
        {/* Trend line */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}
          aria-hidden
        >
          <polyline
            points={linePoints}
            fill="none"
            stroke={LINE_COLOR}
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        {/* Reading dots */}
        {points.map((p, i) => (
          <div
            key={`${p.observedAt}-${i}`}
            title={`${p.value}${unit ? ` ${unit}` : ''} · ${formatShortDate(p.observedAt)}`}
            style={{
              position: 'absolute',
              left: `${xPercent(i)}%`,
              top: `${valueToYPercent(p.value)}%`,
              width: 10,
              height: 10,
              marginLeft: -5,
              marginTop: -5,
              borderRadius: '50%',
              background: p.status.color,
              border: '2px solid var(--melio-cream, #F5F3F0)',
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }}
          />
        ))}
      </div>
      {/* Date axis (first → last) */}
      <div className="flex justify-between mt-1" style={{ fontSize: 10, color: 'var(--melio-text-muted)' }}>
        <span>{formatShortDate(points[0].observedAt)}</span>
        <span>{formatShortDate(points[points.length - 1].observedAt)}</span>
      </div>
    </div>
  );
}
